/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Guardian Approval Page - Handle guardian approval for minor student signups
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  User,
  Mail,
  Calendar,
  Clock
} from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('GuardianApproval')

const GuardianApproval = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [approval, setApproval] = useState(null)
  const [error, setError] = useState(null)
  const [approved, setApproved] = useState(false)
  const [rejected, setRejected] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)

  useEffect(() => {
    fetchApprovalRequest()
  }, [token])

  const fetchApprovalRequest = async () => {
    try {
      setLoading(true)
      const response = await fetch(`/api/guardian-approval/${token}`)
      const data = await response.json()

      if (response.ok && data.success) {
        setApproval(data.approval)
      } else {
        setError(data.message || 'Invalid or expired approval link')
      }
    } catch (err) {
      logger.error('Error fetching approval request:', err)
      setError('Failed to load approval request')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    // Validate terms acceptance
    if (!termsAccepted || !privacyAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue')
      return
    }

    try {
      setProcessing(true)
      const response = await fetch(`/api/guardian-approval/${token}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setApproved(true)
        toast.success('Student account approved successfully!')
      } else {
        throw new Error(data.message || 'Failed to approve account')
      }
    } catch (err) {
      logger.error('Error approving account:', err)
      toast.error(err.message || 'Failed to approve account')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    try {
      setProcessing(true)
      const response = await fetch(`/api/guardian-approval/${token}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setRejected(true)
        toast.success('Account request rejected')
      } else {
        throw new Error(data.message || 'Failed to reject account')
      }
    } catch (err) {
      logger.error('Error rejecting account:', err)
      toast.error(err.message || 'Failed to reject account')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary-600 dark:text-primary-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading approval request...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid Link</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Go to Home
          </Link>
        </motion.div>
      </div>
    )
  }

  if (approved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Account Approved!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The student account for <strong>{approval?.child?.name}</strong> has been approved. They
            can now log in and start using Deaveris.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Go to Home
          </Link>
        </motion.div>
      </div>
    )
  }

  if (rejected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Request Rejected
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The account request has been rejected. The student will be notified.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Go to Home
          </Link>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Guardian Approval Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Your child is requesting permission to create a Deaveris account
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Student Information
          </h2>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                <p className="font-medium text-gray-900 dark:text-white">{approval?.child?.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {approval?.child?.email}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Age</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {approval?.child?.age} years old
                  {approval?.child?.dateOfBirth && (
                    <span className="text-gray-500 dark:text-gray-400 ml-2">
                      (Born {new Date(approval.child.dateOfBirth).toLocaleDateString()})
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Request Date</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(approval?.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-1">
                Next Steps
              </p>
              <ul className="text-xs text-primary-700 dark:text-primary-300 space-y-1">
                <li>• Create your guardian account to approve this request</li>
                <li>• Your child's account will be activated after approval</li>
                <li>• You'll get access to monitor their progress and manage settings</li>
                <li>• Set up billing and manage your family subscription</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleReject}
            disabled={processing}
            className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Processing...
              </>
            ) : (
              'Decline Request'
            )}
          </button>
          <Link
            to={`/register/guardian?approvalToken=${token}`}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md text-center"
          >
            Create Guardian Account & Approve
          </Link>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          This approval link expires on {new Date(approval?.expiresAt).toLocaleString()}
        </p>
      </motion.div>
    </div>
  )
}

export default GuardianApproval
